// Транспорт раздела «Отсутствия»: график, заявки на отпуск и отгул, командировки.
// ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ
// Источник — универсальные списки Bitrix (зеркало bxl_*), а не портал и не смарт-процессы:
// у него свой синк, свои оговорки и свои права. Складывать эти хуки в чужой файл значило бы
// однажды переиспользовать «покажи отпуска» внутри раздела с другими правами.
//
// ЗАПИСИ ЗДЕСЬ НЕТ И ПОЯВИТЬСЯ НЕ ДОЛЖНО: master — Bitrix, и на этих списках висят живые
// бизнес-процессы, которые ставят задачи людям. Ручки записи нет и в ядре.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/absence'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/coordProjects.ts. Продублирован
 * намеренно: общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк
 * значило бы поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Отсутствия» вам не открыт: он показывает свод по всем '
        + 'сотрудникам сразу и зоной ответственности не сужается')
    }
    // 400 от ядра — это неверный фильтр. Пустая таблица читалась бы как «никто не
    // отсутствует», а по такому ответу человеку ставят задачу на завтра.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. absence_api.py) ───────────────

export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

export interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

/**
 * Строка графика отсутствий.
 *
 * `kind` вычисляет ЯДРО, а не экран: правило («отпуск ежегодный», «отпуск декретный» и
 * «отпуск льготный» — это один вид) должно жить там же, где считаются итоги, иначе
 * подпись под таблицей перестанет относиться к таблице.
 *
 * `bad_period` — окончание раньше начала. Ошибка источника, правится в Bitrix и его
 * владельцем; у таких строк `days` пуст, и в сумму дней они не входят.
 */
export interface ОтсутствиеСтрока {
  element_id: number
  user_bx_id: number | null
  fio: string | null
  position: string | null
  department: string | null
  user_active: boolean | null
  type_id: number | null
  type_name: string | null
  kind: string
  date_from: string | null
  date_to: string | null
  days: number | null
  bad_period: boolean
  reason: string | null
}

export interface ОтсутствияИтоги {
  rows: number | null
  people: number | null
  bad_period: number | null
  absences: number | null
  days: number | null
}

/** Заявка на отпуск или отгул. `by_author` — человека не нашли по почте, взят автор. */
export interface ЗаявкаСтрока {
  element_id: number
  user_bx_id: number | null
  fio: string | null
  by_author: boolean
  author_bx_id: number | null
  author_fio: string | null
  request_kind_id: number | null
  request_kind: string | null
  hr_id: number | null
  hr_status: string | null
  company: string | null
  standin_bx_id: number | null
  standin_fio: string | null
  date_from: string | null
  date_to: string | null
  days: number | null
  bad_period: boolean
}

export interface ЗаявкиИтоги {
  rows: number | null
  people: number | null
  no_hr_mark: number | null
  approved: number | null
  declined: number | null
  by_author: number | null
}

/** Заявка на командировку. Денежных полей здесь нет — их не отдаёт ядро. */
export interface КомандировкаСтрока {
  element_id: number
  user_bx_id: number | null
  fio: string | null
  name_in_source: string | null
  sender_bx_id: number | null
  sender_fio: string | null
  trip_kind_id: number | null
  trip_kind: string | null
  status_id: number | null
  status: string | null
  place: string | null
  purpose: string | null
  vessel_ref: string | null
  date_from: string | null
  date_to: string | null
  days: number | null
  bad_period: boolean
}

export interface КомандировкиИтоги {
  rows: number | null
  people: number | null
  no_person: number | null
  approved: number | null
  declined: number | null
  no_status: number | null
  places: number | null
}

export interface ЧеловекВСправочнике {
  bitrix_id: number
  fio: string | null
  active: boolean | null
  n: number | null
}

export interface ОтделВСправочнике {
  bitrix_id: number
  name: string | null
  people: number | null
}

export interface ЗначениеПеречисления {
  enum_id: number | null
  enum_text: string | null
  n?: number | null
}

export interface СводНаДату {
  kind: string
  rows: number | null
  people: number | null
}

/**
 * Итог на дату по РАЗНЫМ людям, посчитанный ядром отдельной строкой.
 *
 * Складывать `people` по видам на экране нельзя: у человека в один день могут стоять и
 * командировка, и отгул, и он посчитался бы дважды. Отсюда отдельное поле, а не сумма.
 */
export interface ИтогНаДату {
  rows: number | null
  people: number | null
  people_absent: number | null
  rows_absent: number | null
}

export interface СправочникиОтсутствий {
  source: string
  counts: Record<string, number | string | null>
  on: string
  on_date: СводНаДату[]
  on_total: ИтогНаДату | null
  freshness: Свежесть[]
  graph_types: ЗначениеПеречисления[]
  people: ЧеловекВСправочнике[]
  departments: ОтделВСправочнике[]
  request_kinds: ЗначениеПеречисления[]
  hr_marks: ЗначениеПеречисления[]
  trip_kinds: ЗначениеПеречисления[]
  trip_statuses: ЗначениеПеречисления[]
  kinds: string[]
  absence_kinds: string[]
  sort_list: string[]
  sort_requests: string[]
  sort_trips: string[]
  data_notes: Record<string, string>
  caveats: string[]
}

export interface СводПоЧеловеку {
  person: {
    bitrix_id: number
    fio: string | null
    position: string | null
    active: boolean | null
    department: string | null
  }
  by_kind: { kind: string; rows: number | null; days: number | null;
             first_day: string | null; last_day: string | null }[]
  by_year: { year: number | null; absences: number | null;
             vacation_days: number | null; sick_days: number | null }[]
  ahead: { kind: string; date_from: string | null; date_to: string | null;
           reason: string | null }[]
  kinds: string[]
  absence_kinds: string[]
}

// ─────────────────────────────── хуки ───────────────────────────────

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useСправочникиОтсутствий(на?: string) {
  return useQuery<СправочникиОтсутствий>({
    queryKey: ['absence', 'refs', на ?? ''],
    queryFn: () => запрос<СправочникиОтсутствий>('/refs' + строкаЗапроса({ on: на })),
    ...ОБЩЕЕ,
  })
}

export function useОтсутствия(ф: Record<string, unknown>) {
  return useQuery<Список<ОтсутствиеСтрока> & { totals: ОтсутствияИтоги }>({
    queryKey: ['absence', 'list', ф],
    queryFn: () => запрос('/list' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useЗаявкиНаОтпуск(ф: Record<string, unknown>) {
  return useQuery<Список<ЗаявкаСтрока> & { totals: ЗаявкиИтоги }>({
    queryKey: ['absence', 'requests', ф],
    queryFn: () => запрос('/requests' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useКомандировки(ф: Record<string, unknown>) {
  return useQuery<Список<КомандировкаСтрока> & { totals: КомандировкиИтоги }>({
    queryKey: ['absence', 'trips', ф],
    queryFn: () => запрос('/trips' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/** Свод по одному человеку. Пустой `user` — не ходим вовсе, а не спрашиваем «ничего». */
export function useСводПоЧеловеку(user?: number) {
  return useQuery<СводПоЧеловеку>({
    queryKey: ['absence', 'person', user],
    queryFn: () => запрос(`/person?user=${user}`),
    enabled: !!user,
    ...ОБЩЕЕ,
  })
}

/**
 * Оговорки раздела, которые экран обязан произнести, и порядок их чтения.
 * Тексты и числа в них считает ядро — здесь только ключи: свой текст неизбежно оброс бы
 * вбитым числом, а оно однажды разойдётся с тем, что человек видит рядом.
 */
export const ОГОВОРКИ_ОТСУТСТВИЙ = [
  'график',
  'заявки_в_графике',
  'два_учёта',
  'отсечка',
  'битый_период',
  'без_типа',
  'заявка_за_другого',
  'нет_времени',
  'деньги',
  'файлы',
  'причина',
]

/** Как называется сущность зеркала в подписи «обновлено N мин назад». */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'bxl:lists': 'каталог списков',
  'bxl:fields': 'описания свойств',
  'bxl:enums': 'справочники значений',
  'bxl:elements': 'записи списков',
  'bxl:values': 'значения свойств',
}

/** Самая старая из отметок свежести: раздел не свежее своей отстающей части. */
export function самаяСтарая(св?: Свежесть[]): Свежесть | undefined {
  if (!св?.length) return undefined
  return [...св].sort((a, b) => (b.age_min ?? 0) - (a.age_min ?? 0))[0]
}
